import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  useGetAdminLearningTrackByIdForEditQuery,
  useUpdateLearningTrackMutation,
  useAddCourseToLearningTrackMutation,
  useRemoveCourseFromLearningTrackMutation,
} from "../../services/organization/adminLearningTrack";
import Details from "../../components/LearningTracks/Admin/MutationPages/Tabs/Details";
import Courses from "../../components/LearningTracks/Admin/MutationPages/Tabs/Courses";
import TabsNav from "../../components/tabs/Tabs";
import Spinner from "../../components/Spinner";
import { useTranslation } from "react-i18next";

export default function EditLearningPath() {
  const TABS = {
    DETAILS: 1,
    COURSES: 2,
  };

  let navigate = useNavigate();
  const { learningPathId } = useParams();
  const [isValidated, setIsValidated] = useState(false);
  const [submmiting, setSubmmiting] = useState(false);
  const [submit, setSubmit] = useState(false);
  const [selectedTab, setSelectedTab] = useState(TABS.DETAILS);
  const [formData, setFormData] = useState(null);
  const { data: learningTrackDetails, isLoading: isLoadingDetails } =
    useGetAdminLearningTrackByIdForEditQuery(learningPathId);
  const [updateLearningTrack] = useUpdateLearningTrackMutation();

  const [courses, setCourses] = useState(
    learningTrackDetails?.data?.courses || [],
  );
  const [deletedCoursesIds, setDeltedCoursesIds] = useState([]);
  const [addCourseToLearningTrack] = useAddCourseToLearningTrackMutation();
  const [removeCourseFromLearningTrack] =
    useRemoveCourseFromLearningTrackMutation();
  let learningTrackCourseIdsSet = new Set(
    learningTrackDetails?.data?.courses?.map((course) => course.id),
  );

  const { t } = useTranslation(["common", "learningPaths"]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    setCourses(learningTrackDetails?.data?.courses);
  }, [learningTrackDetails?.data?.courses]);

  useEffect(() => {
    async function submit() {
      try {
        setSubmmiting(true);
        if (formData) {
          await updateLearningTrack({
            learningTrackId: learningPathId,
            body: formData,
          }).unwrap();
          if (formData.image) {
            const data = new FormData();
            data.append("image", formData.image);
            await updateLearningTrack({
              learningTrackId: learningPathId,
              body: data,
            }).unwrap();
          }
        }
        const courseIds = courses
          .map((course) => course.id)
          .filter((courseId) => !learningTrackCourseIdsSet.has(courseId));
        if (courseIds.length) {
          await addCourseToLearningTrack({
            learningTrackId: learningPathId,
            body: { course_ids: courseIds },
          }).unwrap();
        }
        if (deletedCoursesIds.length) {
          await removeCourseFromLearningTrack({
            learningTrackId: learningPathId,
            body: { course_ids: deletedCoursesIds },
          }).unwrap();
        }

        toast.success(t("successfulSave"));
        navigate(`/admin/learning_paths/${learningPathId}`);
        setSubmmiting(false);
      } catch (e) {
        toast.error(e?.data?.message);
        setSubmmiting(false);
      }
    }
    if (!isValidated) return;
    submit();
  }, [isValidated]);

  function onSubmit() {
    setSubmit(true);
    if (!formData) {
      setIsValidated(true);
    }
  }

  if (isLoadingDetails) {
    return <></>;
  }

  return (
    <div className="admin-learning-path-edit">
      <div className="sticky-header header-table no-padding-b admin-learning-path-edit__header">
        <div className="admin-learning-path-edit__toolbar d-flex align-center flex-wrap">
          <div className="left-side admin-learning-path-edit__title">
            <h1 className="title-page mt-1 mb-0">
              {t("learningPaths:EditPath")}
            </h1>
          </div>

          <div className="ms-auto right-side admin-learning-path-edit__actions">
            <ul className="list-filter">
              <li>
                <button
                  type="button"
                  className="btn btn-main-color font-15 min-w-140"
                  onClick={onSubmit}
                  disabled={submmiting}
                >
                  {t("learningPaths:savePath")}
                  {submmiting && (
                    <Spinner
                      style={{
                        marginInlineStart: "10px",
                        width: "20px",
                        height: "20px",
                      }}
                    />
                  )}
                </button>
              </li>
              <li>
                <button
                  type="button"
                  className="btn btn-grey-color font-15 min-w-140"
                  onClick={() =>
                    navigate(`/admin/learning_paths/${learningPathId}`)
                  }
                >
                  {t("cancel")}
                </button>
              </li>
            </ul>
          </div>
        </div>

        <TabsNav
          activeIndex={selectedTab - 1}
          tabsConfig={[
            {
              name: "learning-track-details",
              label: t("learningPaths:pathDetails"),
              clickHandler: () => setSelectedTab(TABS.DETAILS),
            },
            {
              name: "learning-track-courses",
              label: t("learningPaths:pathCourses"),
              clickHandler: () => setSelectedTab(TABS.COURSES),
            },
          ]}
        />
      </div>

      <div className="admin-learning-path-edit__body">
        {selectedTab === TABS.DETAILS && (
          <Details
            submit={submit}
            setSubmit={setSubmit}
            setIsValidated={setIsValidated}
            formData={formData}
            setFormData={setFormData}
            inputInititalValues={
              learningTrackDetails?.data?.data ??
              learningTrackDetails?.data ??
              learningTrackDetails ??
              null
            }
          />
        )}

        {selectedTab === TABS.COURSES && (
          <Courses
            courses={courses}
            setCourses={setCourses}
            deletedCoursesIds={deletedCoursesIds}
            setDeltedCoursesIds={setDeltedCoursesIds}
            learningTrackId={learningPathId}
          />
        )}
      </div>
    </div>
  );
}
